/*
 * Shipping Protection (Route-style) — cart logic.
 *
 * Pricing model: tiered hidden product (Model A). A single hidden product has
 * one variant per subtotal band. We read the cart subtotal (excluding the
 * protection line itself), pick the matching tier, and keep a single
 * protection line item at quantity 1 in sync with that tier.
 *
 * Default is opt-out (pre-selected like Route). The customer's explicit choice
 * is stored in the `shipping_protection_optin` cart attribute so it survives
 * navigation and lands on the order.
 *
 * All amounts are in cents. Config + the tier table come from theme settings
 * via snippets/shipping-protection.liquid.
 */
(function () {
  'use strict';

  var OPTIN_ATTR = 'shipping_protection_optin';
  var RECOMPUTE_DEBOUNCE_MS = 250;

  // Recursion guard: set while we are mutating the cart so cartUpdate events
  // we (or the theme's refreshers) fire during our own work don't loop us.
  var isApplyingProtection = false;

  /* ----------------------------- config ----------------------------- */

  function getConfig() {
    var el = document.querySelector('script[data-shipping-protection-config]');
    if (!el) return null;
    var cfg;
    try {
      cfg = JSON.parse(el.textContent);
    } catch (e) {
      console.warn('[shipping-protection] invalid config JSON', e);
      return null;
    }
    if (!cfg || !cfg.product_id || !Array.isArray(cfg.tiers) || !cfg.tiers.length) {
      return null;
    }
    cfg.product_id = Number(cfg.product_id);
    cfg.tiers = cfg.tiers
      .map(function (t) {
        return {
          max_subtotal_cents: Number(t.max_subtotal_cents),
          variant_id: Number(t.variant_id),
          price_cents: Number(t.price_cents),
        };
      })
      .filter(function (t) {
        return t.variant_id && !isNaN(t.max_subtotal_cents);
      });
    return cfg.tiers.length ? cfg : null;
  }

  /* --------------------------- pure helpers -------------------------- */

  // Sum of every line except the protection product.
  function protectedSubtotalCents(cart, productId) {
    return cart.items
      .filter(function (i) {
        return i.product_id !== productId;
      })
      .reduce(function (sum, i) {
        return sum + i.final_line_price;
      }, 0);
  }

  // Pick the tier whose band covers the subtotal; tiers ascending, last is catch-all.
  function pickTier(subtotalCents, tiers) {
    var sorted = tiers.slice().sort(function (a, b) {
      return a.max_subtotal_cents - b.max_subtotal_cents;
    });
    return (
      sorted.find(function (t) {
        return subtotalCents <= t.max_subtotal_cents;
      }) || sorted[sorted.length - 1]
    );
  }

  // Match the protection line by product id, or by our hidden flag.
  function findProtectionLine(cart, productId) {
    return cart.items.find(function (i) {
      return i.product_id === productId || (i.properties && i.properties._shipping_protection === 'true');
    });
  }

  function variantIdSet(config) {
    var set = {};
    config.tiers.forEach(function (t) {
      set[String(t.variant_id)] = true;
    });
    return set;
  }

  function formatMoney(cents, currency) {
    var amount = (cents || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD',
      }).format(amount);
    } catch (e) {
      return '$' + amount.toFixed(2);
    }
  }

  /* ------------------------------ cart io ---------------------------- */

  function getCart() {
    return fetch(window.routes.cart_url + '.js', {
      headers: { Accept: 'application/json' },
    }).then(function (r) {
      return r.json();
    });
  }

  var SECTION_IDS = ['cart-drawer', 'cart-items', 'cart-footer', 'cart-icon-bubble'];

  var lastPaintedFingerprint = null;

  function cartFingerprint(cart) {
    if (!cart || !Array.isArray(cart.items)) return '';
    return cart.items
      .map(function (i) {
        return String(i.key) + ':' + String(i.quantity);
      })
      .join('|');
  }

  function runQueued(task) {
    if (window.cartWriteQueue && typeof window.cartWriteQueue.enqueue === 'function') {
      return window.cartWriteQueue.enqueue(task);
    }
    return task();
  }

  function mutationBody(payload, withSections) {
    var body = Object.assign({}, payload);
    if (withSections) {
      body.sections = SECTION_IDS;
      body.sections_url = window.location.pathname;
    }
    return JSON.stringify(body);
  }

  function addProtection(variantId, properties, withSections) {
    var body = mutationBody(
      { items: [{ id: variantId, quantity: 1, properties: properties }] },
      withSections
    );
    return fetch(window.routes.cart_add_url, Object.assign(fetchConfig(), { body: body })).then(function (r) {
      return r.json();
    });
  }

  function removeLine(key, withSections) {
    var body = mutationBody({ id: key, quantity: 0 }, withSections);
    return fetch(window.routes.cart_change_url, Object.assign(fetchConfig(), { body: body })).then(function (r) {
      return r.json();
    });
  }

  function setOptIn(value) {
    var attrs = {};
    attrs[OPTIN_ATTR] = value ? 'true' : 'false';
    var body = JSON.stringify({ attributes: attrs });
    return fetch(window.routes.cart_update_url, Object.assign(fetchConfig(), { body: body })).then(function (r) {
      return r.json();
    });
  }

  /* --------------------------- DOM rendering ------------------------- */

  function parse(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  // Replace a target element's innerHTML from a parsed section, by selector.
  function replaceInner(doc, sourceSelector, targetSelector) {
    var targetEl = document.querySelector(targetSelector);
    var sourceEl = doc.querySelector(sourceSelector);
    if (targetEl && sourceEl) targetEl.innerHTML = sourceEl.innerHTML;
  }

  // Map a section-rendering response onto whatever cart UI is on this page.
  // fingerprint: optional cart key:qty signature; skip paint when unchanged (P2).
  function renderSections(sections, fingerprint) {
    if (!sections) return;
    if (fingerprint && fingerprint === lastPaintedFingerprint) return;

    // Cart drawer: when empty, replace the whole .drawer__inner so the custom
    // empty UI (sampler/CTA) is restored. Partial item/footer swaps leave the
    // filled shell and only toggle is-empty — the bug we hit after clear.
    if (sections['cart-drawer']) {
      var drawerDoc = parse(sections['cart-drawer']);
      var sectionDrawer = drawerDoc.querySelector('cart-drawer');
      var isEmpty = Boolean(sectionDrawer && sectionDrawer.classList.contains('is-empty'));
      var liveDrawer = document.querySelector('cart-drawer');
      if (liveDrawer) liveDrawer.classList.toggle('is-empty', isEmpty);

      if (isEmpty) {
        var targetInner = document.querySelector('#CartDrawer .drawer__inner');
        var sourceInner =
          drawerDoc.querySelector('#CartDrawer .drawer__inner') || drawerDoc.querySelector('.drawer__inner');
        if (targetInner && sourceInner) {
          targetInner.innerHTML = sourceInner.innerHTML;
        }
      } else {
        ['cart-drawer-items', '.cart-drawer__footer'].forEach(function (sel) {
          var target = document.querySelector(sel);
          var source = drawerDoc.querySelector(sel);
          if (target && source) target.replaceWith(source);
        });
      }
    }

    // Full cart page sections (Dawn replaces the .js-contents inner markup).
    if (sections['cart-items']) {
      replaceInner(parse(sections['cart-items']), '.js-contents', '#main-cart-items .js-contents');
    }
    if (sections['cart-footer']) {
      replaceInner(parse(sections['cart-footer']), '.js-contents', '#main-cart-footer .js-contents');
    }

    if (sections['cart-icon-bubble']) {
      replaceInner(parse(sections['cart-icon-bubble']), '.shopify-section', '#cart-icon-bubble');
    }

    if (fingerprint) lastPaintedFingerprint = fingerprint;
  }

  // Keep the cart/drawer empty-state styling in sync after we add or remove the
  // protection line ourselves (the theme only does this for its own mutations).
  function toggleEmptyState(isEmpty) {
    ['cart-drawer', 'cart-items', 'cart-drawer-items'].forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        el.classList.toggle('is-empty', isEmpty);
      });
    });
    var footer = document.getElementById('main-cart-footer');
    if (footer) footer.classList.toggle('is-empty', isEmpty);
  }

  // Flag the protection line's row so CSS can hide its quantity stepper / remove.
  function markProtectionLineRows(config) {
    var variants = variantIdSet(config);
    document.querySelectorAll('.cart-item').forEach(function (row) {
      var input = row.querySelector('[data-quantity-variant-id]');
      var vid = input ? input.getAttribute('data-quantity-variant-id') : null;
      if (vid && variants[String(vid)]) {
        row.setAttribute('data-shipping-protection-line', 'true');
      } else {
        row.removeAttribute('data-shipping-protection-line');
      }
    });
  }

  // Reflect resolved state onto every toggle instance (drawer + cart page).
  function updateToggleUI(config, optedIn, subtotalCents, hasRealItems) {
    var priceCents = 0;
    if (hasRealItems) {
      priceCents = pickTier(subtotalCents, config.tiers).price_cents;
    }
    var priceText = hasRealItems ? formatMoney(priceCents, config.currency) : '';

    document.querySelectorAll('[data-shipping-protection]').forEach(function (container) {
      container.classList.toggle('shipping-protection--hidden', !hasRealItems);

      var checkbox = container.querySelector('[data-shipping-protection-checkbox]');
      if (checkbox) checkbox.checked = optedIn;

      var price = container.querySelector('[data-shipping-protection-price]');
      if (price) price.textContent = priceText;
    });
  }

  /* ------------------------------- apply ----------------------------- */

  function paintFromMutation(res) {
    renderSections(res && res.sections);
    // /cart/add.js returns only added lines — refresh fingerprint from full cart.
    return getCart()
      .then(function (cart) {
        lastPaintedFingerprint = cartFingerprint(cart);
        return cart;
      })
      .catch(function () {
        if (res && Array.isArray(res.items) && res.items[0] && res.items[0].key) {
          // change.js full-cart responses are fine to fingerprint directly.
          lastPaintedFingerprint = cartFingerprint(res);
        }
        return res;
      });
  }


  function resolveOptIn(cart, config) {
    var attr = cart.attributes && cart.attributes[OPTIN_ATTR];
    if (attr === 'true') return true;
    if (attr === 'false') return false;
    return config.default_on !== false;
  }

  // Recompute and reconcile the protection line with the current cart.
  function apply() {
    var config = getConfig();
    if (!config) return Promise.resolve();

    return runQueued(function () {
      // Another apply may have been queued ahead of us; coalesce.
      if (isApplyingProtection) {
        applyPending = true;
        return Promise.resolve();
      }
      isApplyingProtection = true;

      return getCart()
      .then(function (cart) {
        var productId = config.product_id;
        var optedIn = resolveOptIn(cart, config);
        var existing = findProtectionLine(cart, productId);
        var subtotal = protectedSubtotalCents(cart, productId);
        var hasRealItems = subtotal > 0;

        // Off, or nothing to protect: remove any existing line.
        if (!optedIn || !hasRealItems) {
          updateToggleUI(config, optedIn, subtotal, hasRealItems);
          if (existing) {
            return removeLine(existing.key, true).then(function (res) {
              renderSections(res && res.sections, cartFingerprint(res));
              if (res) toggleEmptyState(res.item_count === 0);
              return res;
            });
          }
          lastPaintedFingerprint = cartFingerprint(cart);
          return null;
        }

        var tier = pickTier(subtotal, config.tiers);
        var properties = {
          _shipping_protection: 'true',
          _protected_subtotal: formatMoney(subtotal, config.currency),
          _rate: config.rate_label || '',
        };

        updateToggleUI(config, optedIn, subtotal, hasRealItems);

        // No line yet: add the correct tier.
        if (!existing) {
          return addProtection(tier.variant_id, properties, true).then(function (res) {
            return paintFromMutation(res).then(function () {
              toggleEmptyState(false);
              return res;
            });
          });
        }

        // Subtotal moved to a new band: change.js cannot swap a variant id,
        // so remove the old line then add the new one.
        if (existing.variant_id !== tier.variant_id) {
          return removeLine(existing.key, false).then(function () {
            return addProtection(tier.variant_id, properties, true).then(function (res) {
              return paintFromMutation(res).then(function () {
                return res;
              });
            });
          });
        }

        // Same variant: nothing to change. Remember fingerprint so a later
        // no-op apply does not force a redundant paint if something else asks.
        lastPaintedFingerprint = cartFingerprint(cart);
        return null;
      })
      .then(function (result) {
        // result is null when no mutation — skip DOM paint (caller already avoided renderSections).
        return result;
      })
      .catch(function (e) {
        console.warn('[shipping-protection] apply failed', e);
      })
      .finally(function () {
        isApplyingProtection = false;
        markProtectionLineRows(config);
        if (applyPending) {
          applyPending = false;
          scheduleApply();
        } else {
          if (!recomputeTimer) awaitingApply = false;
          notifySettledIfIdle();
        }
      });
    });
  }

  /* --------------------------- event wiring -------------------------- */

  var recomputeTimer = null;
  var applyPending = false;
  var awaitingApply = false;
  var settleWaiters = [];

  function isProtectionBusy() {
    return awaitingApply || Boolean(recomputeTimer) || isApplyingProtection || applyPending;
  }

  function notifySettledIfIdle() {
    if (isProtectionBusy()) return;
    var waiters = settleWaiters.splice(0);
    waiters.forEach(function (resolve) {
      resolve();
    });
  }

  function whenProtectionSettled() {
    if (!isProtectionBusy()) return Promise.resolve();
    return new Promise(function (resolve) {
      settleWaiters.push(resolve);
    });
  }

  // Run apply, but if a cart change lands while we're mid-flight, remember it
  // and recompute once more when we're done (so we never miss a band change).
  function runApply() {
    if (isApplyingProtection) {
      applyPending = true;
      return;
    }
    // apply()'s finally owns applyPending retry + settle notifications
    apply();
  }

  function scheduleApply() {
    clearTimeout(recomputeTimer);
    awaitingApply = true;
    recomputeTimer = setTimeout(function () {
      recomputeTimer = null;
      runApply();
    }, RECOMPUTE_DEBOUNCE_MS);
  }

  // Delegated handler so it survives drawer/cart DOM re-renders.
  document.addEventListener('change', function (event) {
    var checkbox = event.target.closest && event.target.closest('[data-shipping-protection-checkbox]');
    if (!checkbox) return;

    var config = getConfig();
    if (!config) return;

    var optedIn = checkbox.checked;
    // Reflect the choice on every toggle instance immediately.
    document.querySelectorAll('[data-shipping-protection-checkbox]').forEach(function (cb) {
      cb.checked = optedIn;
    });

    setOptIn(optedIn)
      .then(function () {
        runApply();
      })
      .catch(function (e) {
        console.warn('[shipping-protection] opt-in change failed', e);
      });
  });

  // Recompute whenever the cart changes from any source (qty edit, removal, add).
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      scheduleApply();
    });
  }

  /* ------------------------------- init ------------------------------ */

  function init() {
    var config = getConfig();
    if (!config) return;
    markProtectionLineRows(config);
    // Auto-add on load if the resolved state is on and there are real items.
    apply();
  }

  // Cart UI waits on this so price loaders stay up through SP follow-up writes.
  window.ShippingProtection = {
    isBusy: isProtectionBusy,
    whenSettled: whenProtectionSettled,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();